import { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO, { generateBreadcrumbSchema } from '@/components/SEO';
import { createAppointment } from '@/lib/api';
import { toast } from 'sonner';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { CalendarIcon, Clock, Loader2, CheckCircle } from 'lucide-react';
import { trackFormSubmit } from '@/lib/gtm';

const services = [
  "Content Marketing",
  "Performance Advertising",
  "Marketing Automation",
  "SEO & Organic Growth",
  "Social Media Management",
  "Email Marketing",
  "Full Growth Strategy",
];

const timeSlots = [
  "09:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "02:00 PM",
  "03:00 PM",
  "04:00 PM",
  "05:00 PM",
];

export default function BookAppointment() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    time: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error('Please select a date');
      return;
    }
    
    if (!formData.time) {
      toast.error('Please select a time slot');
      return;
    }

    setLoading(true);

    try {
      await createAppointment({
        ...formData,
        date: format(date, 'yyyy-MM-dd'),
      });
      const filled = Object.values(formData).filter(Boolean).length + 2; // +date +time
      trackFormSubmit('book_appointment_form', filled);
      setSuccess(true);
      toast.success('Appointment booked successfully!');
    } catch (err) {
      toast.error('Failed to book appointment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#050505]" data-testid="booking-success">
        <Navbar />
        <section className="pt-32 pb-24 min-h-[80vh] flex items-center">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <div className="w-20 h-20 bg-[#ff5a1f]/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-[#ff5a1f]" />
            </div>
            <h1 className="font-syne text-4xl font-bold text-white mb-4">
              Booking Confirmed!
            </h1>
            <p className="text-[#888] text-lg mb-8">
              We've received your appointment request. Our team will reach out to confirm the details soon.
            </p>
            <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-8 text-left">
              <h3 className="font-syne font-bold text-white mb-4">Appointment Details</h3>
              <div className="space-y-3 text-sm">
                <p><span className="text-[#888]">Date:</span> <span className="text-white">{date && format(date, 'PPP')}</span></p>
                <p><span className="text-[#888]">Time:</span> <span className="text-white">{formData.time}</span></p>
                <p><span className="text-[#888]">Service:</span> <span className="text-white">{formData.service}</span></p>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="book-appointment-page">
      <SEO 
        title="Book a Free Consultation"
        description="Schedule a free 30-minute growth consultation with Wizbang. Discuss your marketing goals and get a custom roadmap for revenue growth."
        keywords="book consultation, free marketing consultation, growth strategy call, digital marketing appointment"
        url="/book-appointment"
        structuredData={generateBreadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Book Appointment", path: "/book-appointment" }
        ])}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-gradient-radial opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <p className="text-[#ff5a1f] font-mono text-sm uppercase tracking-widest mb-6">
            // Book a Call
          </p>
          <h1 className="font-syne text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Schedule Your
            <br />
            <span className="text-[#ff5a1f]">Growth Call</span>
          </h1>
          <p className="text-[#888] text-lg md:text-xl max-w-2xl">
            Book a free 30-minute consultation. Let's discuss your goals and create a roadmap for growth.
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-24" data-testid="booking-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <form onSubmit={handleSubmit} data-testid="booking-form">
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Calendar & Time */}
              <div>
                <h2 className="font-syne text-2xl font-bold text-white mb-8 flex items-center gap-3">
                  <CalendarIcon className="w-6 h-6 text-[#ff5a1f]" />
                  Select Date & Time
                </h2>

                <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-6 mb-8">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return d < today || d.getDay() === 0;
                    }}
                    fromDate={new Date()}
                    className="rounded-md"
                    data-testid="booking-calendar"
                  />
                </div>

                {date && (
                  <div>
                    <h3 className="font-syne font-bold text-white mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-[#ff5a1f]" />
                      Available Time Slots for {format(date, 'MMM d, yyyy')}
                    </h3>
                    <div className="grid grid-cols-4 gap-3">
                      {timeSlots.map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setFormData({ ...formData, time: slot })}
                          data-testid={`time-slot-${slot.replace(/\s+/g, '-')}`}
                          className={`py-3 text-sm font-medium transition-all ${
                            formData.time === slot
                              ? 'bg-[#ff5a1f] text-white'
                              : 'bg-[#121212] border border-[#333] text-[#888] hover:border-[#ff5a1f] hover:text-white'
                          }`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Contact Details */}
              <div>
                <h2 className="font-syne text-2xl font-bold text-white mb-8">
                  Your Details
                </h2>

                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[#888] text-sm mb-2">Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="booking-name-input"
                        className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-[#888] text-sm mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="booking-email-input"
                        className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[#888] text-sm mb-2">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        data-testid="booking-phone-input"
                        className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors"
                        placeholder="Your phone number"
                      />
                    </div>
                    <div>
                      <label className="block text-[#888] text-sm mb-2">Company</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        data-testid="booking-company-input"
                        className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors"
                        placeholder="Your company name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#888] text-sm mb-2">Service Interest *</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      data-testid="booking-service-select"
                      className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors"
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#888] text-sm mb-2">Additional Notes</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      data-testid="booking-message-input"
                      className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors resize-none"
                      placeholder="Tell us about your goals..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !date || !formData.time}
                    data-testid="booking-submit-btn"
                    className="w-full bg-[#ff5a1f] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#e04e1a] transition-colors duration-300 btn-press flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Booking...
                      </>
                    ) : (
                      'Confirm Booking'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
